import { createContext, useState, useEffect, useContext, useCallback, ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/common/supabase';
import type { Session, User } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';

interface ProfileData {
    id: string;
    role: 'job_seeker' | 'employer' | null;
    company_id: string | null;
    company_role: 'admin' | 'recruiter' | null;
    company_approval_status: 'pending' | 'approved' | 'denied' | 'pending_verification' | null;
    needs_onboarding: boolean;
    full_name: string | null;
    avatar_url: string | null;
    is_internal_admin: boolean;
    active_subscription_plan?: string | null;
    completed_challenges_count?: number;
    is_public?: boolean;
    email_notifications_enabled?: boolean;
    message_notifications?: boolean;
    job_match_notifications?: boolean;
    visibility_mode?: 'private' | 'showcase' | 'open';
    analytics_enabled?: boolean;
    stripe_subscription_status?: string | null;
    stripe_subscription_id?: string | null;
    subscription_period_end?: string | null;
    current_plan_start_date?: string | null;
    stripe_customer_id?: string | null;
    is_active?: boolean;
    deleted_at?: string | null;
}

interface AuthContextType {
    session: Session | null;
    user: User | null;
    profile: ProfileData | null;
    isLoadingInitial: boolean;
    isLoadingProfile: boolean;
    isProcessingAuth: boolean;
    isAuthenticated: boolean;
    isInternalAdmin: boolean;
    signOut: () => Promise<void>;
    refreshUserProfile: () => Promise<ProfileData | null>;
    isMobile: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [isProcessingAuth, setIsProcessingAuth] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const activeFetchAbortControllerRef = useRef<AbortController | null>(null);
    const userRef = useRef(user);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    useEffect(() => { userRef.current = user; }, [user]);

    const profileRef = useRef(profile);

    useEffect(() => { profileRef.current = profile; }, [profile]);

    const isLoadingInitialRef = useRef(isLoadingInitial);

    useEffect(() => { isLoadingInitialRef.current = isLoadingInitial; }, [isLoadingInitial]);


    const fetchProfile = useCallback(async (userId: string): Promise<ProfileData | null> => {
        setIsLoadingProfile(true);
        if (activeFetchAbortControllerRef.current) {
            activeFetchAbortControllerRef.current.abort("New profile fetch initiated");
        }
        const controller = new AbortController();
        activeFetchAbortControllerRef.current = controller;
        let fetchedProfileData: ProfileData | null = null;
        let timeoutId: NodeJS.Timeout;
        try {
            const fetchPromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();
            const timeoutPromise = new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => {

                    reject(new Error('Timeout'));
                }, 500);
            });
            try {
                const result = await Promise.race([fetchPromise, timeoutPromise]);
                const { data: profileData, error: profileError } = result as any;
                if (profileError) {
                    fetchedProfileData = null;
                } else if (profileData) {
                    if (profileData.is_active === false) {
                        await supabase.auth.signOut();
                        fetchedProfileData = null;
                    } else {
                        fetchedProfileData = profileData as ProfileData;
                    }
                }
            } catch (timeoutError) {
                const existingProfile = profileRef.current;
                if (existingProfile && existingProfile.id === userId) {
                    fetchedProfileData = existingProfile;
                }
            }
            clearTimeout(timeoutId!);
        } catch (error: any) {
            fetchedProfileData = null;
        } finally {
            if (activeFetchAbortControllerRef.current === controller) {
                activeFetchAbortControllerRef.current = null;
            }
            setIsLoadingProfile(false);
        }
        return fetchedProfileData;
    }, []);

    const refreshUserProfile = useCallback(async (): Promise<ProfileData | null> => {
        const currentUserId = userRef.current?.id;
        if (!currentUserId) {
            setProfile(null);
            return null;
        }
        const refreshed = await fetchProfile(currentUserId);
        setProfile(refreshed);
        return refreshed;
    }, [fetchProfile]);

    useEffect(() => {
        setIsLoadingInitial(true);
        supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
            setSession(currentSession);
            const initialUser = currentSession?.user ?? null;
            setUser(initialUser);
            if (initialUser) {
                const initialProfile = await fetchProfile(initialUser.id);
                setProfile(initialProfile);
            } else {
                setProfile(null);
            }
        }).catch(_error => {
            setSession(null); setUser(null); setProfile(null);
        }).finally(() => {
            setIsLoadingInitial(false);
        });
    }, [fetchProfile]);

   useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newAuthSession) => {
                setIsProcessingAuth(true);
                try {
                    const eventUser = newAuthSession?.user ?? null;
                    const previousUser = userRef.current;
                    const previousProfile = profileRef.current;
                    setSession(newAuthSession);
                    setUser(eventUser);
                    if (event === 'SIGNED_IN') {
                        if (eventUser) {
                            const fetched = await fetchProfile(eventUser.id);
                            if (fetched) {
                                setProfile(fetched);
                                const currentPath = window.location.pathname;
                                const isOnAdmin = currentPath.startsWith('/admin');
                                const isOnSelectRole = currentPath.includes('/select-role');
                                const isOnAssociateCompany = currentPath.includes('/associate-company');
                                const publicOnlyPaths = ['/', '/signin', '/signup', '/signin-sso', '/accept-invite', '/auth/reset-password'];
                                const isOnPublicPath = publicOnlyPaths.includes(currentPath);
                                if (fetched.is_internal_admin && !isOnAdmin) {
                                    navigate('/admin/tools', { replace: true });
                                } else if (fetched.needs_onboarding && !isOnSelectRole && !isOnAssociateCompany) {
                                    navigate('/dashboard/select-role', { replace: true });
                                } else if (!fetched.needs_onboarding && isOnPublicPath) {
                                    navigate('/dashboard/profile', { replace: true, state: { showWelcome: true, isReturningUser: true } });
                                }
                            }
                        } else {
                            setProfile(null);
                        }
                    } else if (event === 'SIGNED_OUT') {
                        setProfile(null);
                    } else if (event === 'USER_UPDATED') {
                        if (eventUser && eventUser.id === previousUser?.id) {
                            const refreshed = await fetchProfile(eventUser.id);
                            setProfile(refreshed);
                        } else if (eventUser) {
                            const fetched = await fetchProfile(eventUser.id);
                            setProfile(fetched);
                        }
                    } else if (event === 'INITIAL_SESSION') {
                        if (!isLoadingInitialRef.current) {
                            if (eventUser) {
                                if (eventUser.id !== previousUser?.id || !previousProfile) {
                                    const fetched = await fetchProfile(eventUser.id);
                                    setProfile(fetched);
                                }
                            } else if (previousUser) {
                                setProfile(null);
                            }
                        }
                    }
                } finally {
                    setIsProcessingAuth(false);
                }
            }
        );
        return () => {
            subscription?.unsubscribe();
        };
    }, [fetchProfile, navigate]);

   useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible' && !isLoadingInitialRef.current) {
                setIsProcessingAuth(true);
                try {
                    const { data: { session: currentSupabaseSession } } = await supabase.auth.getSession();
                    const currentSupabaseUser = currentSupabaseSession?.user ?? null;
                    const userInContext = userRef.current;
                    const profileInContext = profileRef.current;
                    setSession(currentSupabaseSession);
                    setUser(currentSupabaseUser);
                    if (currentSupabaseUser?.id !== userInContext?.id) {
                        if (currentSupabaseUser) {
                            const visibilityProfile = await fetchProfile(currentSupabaseUser.id);
                            setProfile(visibilityProfile);
                        } else {
                            setProfile(null);
                        }
                    } else if (currentSupabaseUser && !profileInContext) {
                        const visibilityProfile = await fetchProfile(currentSupabaseUser.id);
                        setProfile(visibilityProfile);
                    }
                } finally {
                    setIsProcessingAuth(false);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchProfile]);

    const signOut = async () => {
        setIsProcessingAuth(true);
        const { error } = await supabase.auth.signOut();
        if (error) console.error("AuthContext: Error during sign out");
    };

    const contextValue: AuthContextType = {
        session, user, profile,
        isLoadingInitial,
        isMobile,
        isLoadingProfile,
        isProcessingAuth,
        isAuthenticated: !!user && !!session,
        isInternalAdmin: !!user && !!profile?.is_internal_admin,
        signOut, refreshUserProfile,
    };

    if (isLoadingInitial) {
        return (
            <div className="flex items-center justify-center h-screen w-screen fixed inset-0 bg-background z-[9999]">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
        );
    }
    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};